import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { OrderPlacingResponse } from '@models';
import * as Sentry from '@sentry/angular';

import { AuthService } from './auth.service';
import { UserService } from './user.service';

/**
 * Points in the shopping flow that generate escalations from Chase and therefore
 * need to be searchable by journey id or email.
 */
export type FailurePoint = 'get_user' | 'place_order' | 'place_order_decline';

interface HttpContext {
  url: string;
  method: string;
  status: number;
  /** W3C trace id, the link to the backend log context (RID-1197). */
  traceId: string | null;
  title: string | null;
  detail: string | null;
  errors: string[] | null;
}

/**
 * Carries the failure to Sentry.
 *
 * An `HttpErrorResponse` cannot be reported directly: it does not extend `Error`, so
 * Sentry has no type and no stack to work with and files it under `<unknown>` with a
 * generic "Http failure response for ..." message, indistinguishable from everything
 * else the global `ErrorHandler` reports. Naming the error after the failure point
 * puts the reason in the issue title instead — `get_user: 500 Internal Server Error
 * — /api/user`.
 */
class MonitoredFailure extends Error {
  constructor(failurePoint: FailurePoint, reason: string) {
    super(reason);
    this.name = failurePoint;
  }
}

/**
 * The API returns RFC 9110 problem details on errors, carrying the trace id in the
 * body — the response headers only hold an all-zero `x-ms-middleware-request-id`
 * placeholder from Azure, which correlates nothing.
 */
interface ProblemDetails {
  title?: unknown;
  detail?: unknown;
  traceId?: unknown;
  /** Flat list used by the user endpoint. */
  Errors?: unknown;
  /** Per-field map used by validation failures. */
  errors?: unknown;
}

/**
 * Reports checkout / profile failures to Sentry enriched with the shopping journey
 * id and the user email, so an escalation from Chase can be traced back to a
 * concrete session (RID-1187 / RID-1202).
 *
 * Personally identifiable data is attached to these events only — the global Sentry
 * scope, session replays and performance transactions stay PII-free.
 */
@Injectable({
  providedIn: 'root',
})
export class TelemetryService {
  private readonly authService = inject(AuthService);
  private readonly injector = inject(Injector);

  captureHttpFailure(failurePoint: FailurePoint, error: HttpErrorResponse, method: string): void {
    const problem = this.readProblemDetails(error);
    const http: HttpContext = {
      url: error.url ?? 'unknown',
      method,
      status: error.status,
      traceId: this.asText(problem?.traceId) ?? error.headers?.get('traceparent') ?? null,
      title: this.asText(problem?.title),
      detail: this.asText(problem?.detail),
      errors: this.readErrors(problem),
    };

    this.capture(failurePoint, new MonitoredFailure(failurePoint, this.describe(http)), http);
  }

  /**
   * The order endpoint answers 200 with an `errors` payload when an order is
   * declined. It is not an HTTP failure, but these are exactly the cases Chase
   * escalates, so they are reported as errors too.
   */
  captureOrderDecline(response: OrderPlacingResponse): void {
    const reason = response.errors?.length ? response.errors.join('; ') : 'no reason given';

    this.capture('place_order_decline', new MonitoredFailure('place_order_decline', `declined — ${reason}`), {
      url: 'orders',
      method: 'POST',
      status: 200,
      traceId: null,
      title: null,
      detail: null,
      errors: response.errors?.length ? response.errors : null,
    });
  }

  private capture(failurePoint: FailurePoint, error: unknown, http: HttpContext): void {
    // Reporting a failure must never turn into a second one: this runs inside error
    // paths that the user is already living through.
    try {
      Sentry.withScope((scope) => {
        const journeyId = this.authService.journeyId;
        const email = this.readEmail();

        scope.setUser({ id: journeyId ?? undefined, email: email ?? undefined });
        // Duplicated as tags on purpose: tags are filterable and aggregatable in the
        // Sentry UI, `user.*` fields are not.
        scope.setTag('shopping_journey_id', journeyId ?? 'unknown');
        scope.setTag('failure_point', failurePoint);
        scope.setTag('trace_id', http.traceId ?? 'unknown');
        scope.setContext('http', { ...http });
        scope.setContext('journey', { journeyId, email });
        scope.setLevel('error');
        scope.setFingerprint(['{{ default }}', failurePoint, String(http.status)]);

        Sentry.captureException(error);
      });
    } catch {
      // Intentionally silent — telemetry is not worth breaking the flow for.
    }
  }

  /**
   * The token carries no email claim, so it comes from the loaded profile instead —
   * absent exactly when `/api/user` is the thing that failed.
   *
   * Resolved lazily: injecting `UserService` as a field would instantiate it from
   * the interceptor on the very first request and kick off its profile fetch earlier
   * than the app does. Reading the signal can also rethrow a previous profile error,
   * hence the guard.
   */
  private readEmail(): string | null {
    try {
      return this.injector.get(UserService).emailAddress() ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Builds the one-line reason shown as the issue title, e.g.
   * `500 Internal Server Error — /api/user` or `network or CORS failure — /api/user`.
   * Status 0 means the browser never got a response (offline, CORS, blocked request),
   * so there is no status text to report.
   */
  private describe(http: HttpContext): string {
    const reason = http.status === 0 ? 'network or CORS failure' : [http.status, http.title].filter(Boolean).join(' ');

    return `${reason} — ${this.pathOf(http.url)}`;
  }

  private pathOf(url: string): string {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }

  private readProblemDetails(error: HttpErrorResponse): ProblemDetails | null {
    const body: unknown = error.error;
    return body && typeof body === 'object' ? (body as ProblemDetails) : null;
  }

  private asText(value: unknown): string | null {
    return typeof value === 'string' && value ? value : null;
  }

  /**
   * Two shapes are in play: a flat `Errors` list (used by the user endpoint, see
   * `userErrorInterceptor`) and a per-field `errors` map from validation failures.
   *
   * Only the messages are taken — never the request payload, which carries payment
   * and address data.
   */
  private readErrors(problem: ProblemDetails | null): string[] | null {
    if (!problem) {
      return null;
    }

    if (Array.isArray(problem.Errors)) {
      return problem.Errors.length ? problem.Errors.map(String) : null;
    }

    const fields = problem.errors;
    if (Array.isArray(fields)) {
      return fields.length ? fields.map(String) : null;
    }

    if (fields && typeof fields === 'object') {
      const messages = Object.entries(fields).flatMap(([field, value]) =>
        (Array.isArray(value) ? value : [value]).map((message) => `${field}: ${String(message)}`),
      );
      return messages.length ? messages : null;
    }

    return null;
  }
}
